import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import LabeledControl from "@editor/components/common/designSystem/LabeledControl";
import { useModal } from "@hooks/useModal";

import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";

export interface MinimalTipTapLinkEditorModalProps {
  linkUrl: string;
  linkText: string;
  onSave: (url: string, text: string) => void;
  onCancel?: () => void;
}

export const MinimalTipTapLinkEditorModal: React.FC<
  MinimalTipTapLinkEditorModalProps
> = ({
  linkUrl: initialLinkUrl,
  linkText: initialLinkText,
  onSave,
  onCancel,
}) => {
  const { closeModal } = useModal();
  const [linkUrl, setLinkUrl] = React.useState(initialLinkUrl);
  const [linkText, setLinkText] = React.useState(initialLinkText);

  const handleSave = React.useCallback(() => {
    onSave(linkUrl, linkText);
    closeModal({ type: "minimalTipTapLinkEditorModal" });
  }, [onSave, linkUrl, linkText, closeModal]);

  const handleCancel = React.useCallback(() => {
    onCancel?.();
    closeModal({ type: "minimalTipTapLinkEditorModal" });
  }, [onCancel, closeModal]);

  const handleModalKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        handleSave();
      } else if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  return (
    <Modal
      isOpen={true}
      onOpenChange={(open) => {
        if (!open) {
          handleCancel();
        }
      }}
      title="Add link"
      size="base"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="base" onClick={handleSave}>
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <LabeledControl label="Text">
          <Input
            type="text"
            size="base"
            value={linkText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setLinkText(e.target.value)
            }
            placeholder="Add link text"
            autoFocus={!linkText}
            onKeyDown={handleModalKeyDown}
          />
        </LabeledControl>
        <LabeledControl label="Link">
          <Input
            type="url"
            size="base"
            value={linkUrl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setLinkUrl(e.target.value)
            }
            placeholder="Link URL"
            autoFocus={!linkText}
            onKeyDown={handleModalKeyDown}
          />
        </LabeledControl>
      </div>
    </Modal>
  );
};
